import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { BTN_PRIMARY, BTN_SECONDARY } from "./buttonStyles";
import { emitStayCheckedOut } from "@/hooks/useStayCheckedOut";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Info } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { docsAPI, fmtINR } from "@/lib/documents";
import { GuestInfoStrip } from "@/components/pms/GuestInfoStrip";
import { PreflightStep } from "./PreflightStep";
import { BillStep } from "./BillStep";
import { PaymentStep } from "./PaymentStep";
import { ConfirmStep, DoneStep } from "./ConfirmStep";
import { CreditResolutionStep } from "./CreditResolutionStep";
import { REFERENCE_REQUIRED } from "@/components/pms/AdvancePaymentInput";

const STEPS = ["preflight", "bill", "payment", "confirm", "done"];
const TITLES = { preflight: "Checkout Pre-flight", bill: "Provisional Bill", payment: "Collect Payment", confirm: "Confirm Checkout", done: "Checkout Complete" };

/**
 * THE single checkout flow. Every entry point (queue / session page, stay detail, room drawer,
 * guest workspace) renders this with a stayId. Steps: preflight → bill → payment → confirm → done.
 */
export function CheckoutFlow({ stayId, hotelId, open = true, onSuccess, onCancel }) {
  const [step, setStep] = useState("preflight");
  const [pf, setPf] = useState(null);
  const [summary, setSummary] = useState(null);
  const [bills, setBills] = useState([]);
  const [ack, setAck] = useState(false);
  const [collected, setCollected] = useState(0);
  const [receiptId, setReceiptId] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [credit, setCredit] = useState({ action: null, method: "cash", reference: "" });

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get(`/pms/stays/${stayId}/preflight`).then((r) => r.data), docsAPI.folioSummary(stayId).catch(() => null)]);
      setPf(p); setSummary(s);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [stayId]);

  useEffect(() => {
    if (!open || !stayId) return;
    setStep("preflight"); setPf(null); setAck(false); setCollected(0); setReceiptId(null); setResult(null); setCredit({ action: null, method: "cash", reference: "" });
    load();
  }, [open, stayId, load]);

  const balance = pf?.balance_due > 0 ? pf.balance_due : 0;
  const creditDue = pf?.credit_balance > 0 ? pf.credit_balance : 0;
  const creditBlocker = creditDue > 0 && (!credit.action || (credit.action === "refund" && (!credit.method || (REFERENCE_REQUIRED.has(credit.method) && !credit.reference.trim()))));
  const blocking = pf?.checks.filter((c) => c.blocking && c.status !== "pass") || [];
  const warnings = pf?.checks.filter((c) => !c.blocking && c.status !== "pass") || [];
  const canContinue = pf && pf.stay_status === "checked_in" && blocking.length === 0 && (warnings.length === 0 || ack);
  const finalBill = bills.find((b) => b.status === "finalised") || bills.find((b) => b.status !== "void");

  const next = () => {
    if (step === "preflight") setStep("bill");
    else if (step === "bill") setStep(balance > 0 ? "payment" : "confirm");
    else if (step === "payment") setStep("confirm");
  };
  const back = () => {
    if (step === "bill") setStep("preflight");
    else if (step === "payment") setStep("bill");
    else if (step === "confirm") setStep(collected > 0 || balance > 0 ? "payment" : "bill");
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pms/stays/${stayId}/checkout`, { hotel_id: hotelId || pf.hotel_id, acknowledged_warnings: warnings.map((w) => w.id),
        ...(creditDue > 0 ? { credit_action: credit.action, refund_method: credit.action === "refund" ? credit.method : null, refund_reference: credit.action === "refund" ? credit.reference.trim() || null : null } : {}) });
      setResult(data); setStep("done"); toast.success("Guest checked out");
      emitStayCheckedOut({ stay_id: stayId, room_number: pf.room_number, guest_name: pf.guest_name });
    } catch (e) { toast.error(formatApiError(e)); await load(); if (pf && blocking.length) setStep("preflight"); }
    setBusy(false);
  };

  const disabledReason = (() => {
    if (!pf) return null;
    if (pf.stay_status !== "checked_in") return `Stay is ${pf.stay_status.replace(/_/g, " ")} — checkout not possible`;
    if (step === "preflight") {
      if (blocking.length) return `${blocking.length} hard block${blocking.length === 1 ? "" : "s"} must be resolved: ${blocking.map((b) => b.label.toLowerCase()).join(", ")}`;
      if (warnings.length && !ack) return `Tick the acknowledgement for ${warnings.length} warning${warnings.length === 1 ? "" : "s"} to continue`;
    }
    if (step === "confirm") {
      if (blocking.length) return `${blocking.length} hard block${blocking.length === 1 ? "" : "s"} reappeared — go back to pre-flight`;
      if (balance > 0) return `Collect ${fmtINR(balance)} before confirming checkout`;
      if (creditBlocker) return `Choose what to do with the ${fmtINR(creditDue)} credit before confirming`;
    }
    return null;
  })();

  const close = (v) => { if (!v) { if (step === "done") onSuccess?.(result); else onCancel?.(); } };
  const idx = STEPS.indexOf(step);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-white border border-[#D5E6E8] md:max-w-[680px] max-h-[92vh] overflow-y-auto" data-hocp-dialog data-testid="checkout-flow">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">{TITLES[step]}
            {step !== "done" && <span className="text-[11px] mono font-normal text-[#97A6A9]">Step {idx + 1} of 4</span>}
          </DialogTitle>
          <DialogDescription className="sr-only">Unified checkout flow</DialogDescription>
        </DialogHeader>

        {!pf ? (
          <div className="py-8 text-center text-[13px] text-[#97A6A9] flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" />Running pre-flight…</div>
        ) : (
          <div className="space-y-4">
            {step !== "done" && (
              <GuestInfoStrip compact name={pf.guest_name} phone={pf.guest_phone} email={pf.guest_email} testid="checkout-guest-strip"
                right={<span className="text-[12px] mono text-[#3F5257] whitespace-nowrap" data-testid="checkout-room-chip">Room {pf.room_number || "—"}{pf.room_type ? ` · ${pf.room_type}` : ""}</span>} />
            )}
            {pf.stay_status !== "checked_in" && step !== "done" && (
              <div className="rounded-md bg-[#FEE2E2] text-[#991B1B] px-3 py-2 text-[13px] font-semibold" data-testid="checkout-invalid-status">This stay is {pf.stay_status.replace(/_/g, " ")} — checkout is not possible.</div>
            )}
            {step === "preflight" && <PreflightStep pf={pf} onChanged={load} ack={ack} setAck={setAck} onCollect={() => setStep("payment")} />}
            {step === "bill" && <BillStep stayId={stayId} onBillChange={setBills} />}
            {step === "payment" && (balance > 0
              ? <PaymentStep folioId={pf.folio_id} balance={balance} onPaid={async ({ receipt, amount }) => { setCollected((c) => c + amount); if (receipt?.id) setReceiptId(receipt.id); await load(); setStep("confirm"); }} />
              : <div className="rounded-md bg-[#DBFCEF] text-[#014751] px-3 py-2 text-[13px] font-semibold flex items-center gap-2" data-testid="payment-settled"><CheckCircle2 size={15} />Nothing to collect — folio is fully settled.</div>)}
            {step === "confirm" && <ConfirmStep pf={pf} summary={summary} collectedNow={collected} />}
            {step === "confirm" && creditDue > 0 && <CreditResolutionStep credit={creditDue} value={credit} onChange={setCredit} />}
            {step === "done" && <DoneStep pf={pf} result={result} receiptId={receiptId} billId={finalBill?.id} onDone={() => onSuccess?.(result)} />}
          </div>
        )}

        {pf && step !== "done" && (
          <DialogFooter className="gap-2 sm:justify-between">
            <div className="text-[12px] text-[#3F5257] self-center flex items-start gap-1.5 max-w-[340px]" data-testid="checkout-disabled-reason">
              {disabledReason && <><Info size={14} className="mt-0.5 shrink-0 text-[#97A6A9]" />{disabledReason}</>}
            </div>
            <div className="flex gap-2">
              {step === "preflight" ? <button type="button" className={BTN_SECONDARY} onClick={() => onCancel?.()} data-testid="checkout-cancel">Cancel</button>
                : <button type="button" className={BTN_SECONDARY} onClick={back} data-testid="checkout-back"><ArrowLeft size={14} />Back</button>}
              {step === "preflight" && <button type="button" onClick={next} disabled={!canContinue} className={BTN_PRIMARY} data-testid="checkout-continue">Continue to Bill<ArrowRight size={14} /></button>}
              {step === "bill" && <button type="button" onClick={next} className={BTN_PRIMARY} data-testid="checkout-continue">{balance > 0 ? "Continue to Payment" : "Continue to Confirm"}<ArrowRight size={14} /></button>}
              {step === "payment" && balance <= 0 && <button type="button" onClick={next} className={BTN_PRIMARY} data-testid="checkout-continue">Continue<ArrowRight size={14} /></button>}
              {step === "confirm" && (
                <button type="button" onClick={confirm} disabled={busy || balance > 0 || blocking.length > 0 || creditBlocker} className={BTN_PRIMARY} data-testid="checkout-confirm">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}Confirm Checkout
                </button>
              )}
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
